import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DocumentCheckIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { packageBuilderApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/ui';
import FeatureGate from '../components/FeatureGate';

const SECTIONS = [
  { id: 'marketing', label: 'Marketing Compliance', desc: 'Examiner reports, Marketing & Outreach workbook, approvals, assets' },
  { id: 'cra', label: 'CRA', desc: 'Assessment areas, lending analysis, CD activities, gap analysis' },
  { id: 'fair-lending', label: 'Fair Lending', desc: 'HMDA + ALERT findings, IFLEP summary of risks' },
];

function StatusPill({ status }: { status: string }) {
  if (status === 'READY') return <StatusBadge variant="success">Ready</StatusBadge>;
  if (status === 'FAILED') return <StatusBadge variant="danger">Failed</StatusBadge>;
  return <StatusBadge variant="warning">Generating…</StatusBadge>;
}

export default function ComprehensiveExamPackagePage() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>(['marketing', 'cra', 'fair-lending']);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeBoard, setIncludeBoard] = useState(true);
  const [includeBoardDocx, setIncludeBoardDocx] = useState(false);

  const { data: packages = [], isLoading } = useQuery({
    queryKey: ['compliance-packages', teamId],
    queryFn: async () => (await packageBuilderApi.listPackages(teamId!)).data as any[],
    enabled: !!teamId,
    // Poll while any package is still being assembled.
    refetchInterval: (query) => {
      const arr = query.state.data as any[] | undefined;
      return arr?.some((p) => p.status === 'PENDING' || p.status === 'PROCESSING') ? 4000 : false;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () =>
      packageBuilderApi.createPackage(teamId!, {
        sections: selected,
        scope: { periodStart: startDate || undefined, periodEnd: endDate || undefined },
        outputs: { examZip: true, boardPdf: includeBoard, boardDocx: includeBoardDocx },
      }),
    onSuccess: () => {
      toast.success('Package generation started — this can take a minute.');
      queryClient.invalidateQueries({ queryKey: ['compliance-packages'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to start package'),
  });

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const download = async (id: string, artifact: 'exam' | 'board' | 'board-docx' = 'exam') => {
    try {
      const { data } = await packageBuilderApi.getDownloadUrl(teamId!, id, artifact);
      if (data?.url) window.location.href = data.url;
    } catch {
      toast.error('Download not available yet');
    }
  };

  return (
    <FeatureGate feature="exam_package" mode="lock">
      <div className="space-y-6">
        <PageHeader
          title="Comprehensive Exam Package"
          description="Select the modules to include and generate one exam-ready package pulling from across Clarid."
        />

        {/* Builder */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-ocean-50 to-blue-50">
            <h2 className="text-lg font-semibold text-navy-900">Build a package</h2>
            <p className="text-sm text-gray-600 mt-1">Pick the sections to include and, optionally, a date range.</p>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sections</label>
              <div className="grid gap-2 sm:grid-cols-3">
                {SECTIONS.map((s) => {
                  const on = selected.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${on ? 'border-ocean-400 bg-ocean-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <input type="checkbox" className="mt-0.5 accent-ocean-600" checked={on} onChange={() => toggle(s.id)} />
                      <span>
                        <span className="block text-sm font-medium text-navy-900">{s.label}</span>
                        <span className="block text-xs text-gray-500">{s.desc}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-400">The audit trail and a package summary are always included.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start date (optional)</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End date (optional)</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="accent-ocean-600"
                  checked={includeBoard}
                  onChange={(e) => setIncludeBoard(e.target.checked)}
                />
                Also generate a board-ready PDF summary
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="accent-ocean-600"
                  checked={includeBoardDocx}
                  onChange={(e) => setIncludeBoardDocx(e.target.checked)}
                />
                …and an editable Word (DOCX) version
              </label>
            </div>

            <button
              onClick={() => createMutation.mutate()}
              disabled={selected.length === 0 || createMutation.isPending || !teamId}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50"
            >
              <DocumentCheckIcon className="w-4 h-4 mr-2" />
              {createMutation.isPending ? 'Starting…' : 'Generate package'}
            </button>
          </div>
        </div>

        {/* History */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-navy-900">Recent packages</h2>
          </div>
          {isLoading ? (
            <LoadingSpinner size="md" className="py-12" />
          ) : packages.length === 0 ? (
            <EmptyState
              icon={DocumentCheckIcon}
              title="No packages yet"
              description="Generate your first comprehensive exam package above."
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {packages.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-6 py-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-navy-900">
                        {new Date(p.createdAt).toLocaleString()}
                      </span>
                      <StatusPill status={p.status} />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(p.sections as string[]).map((s) => (
                        <span key={s} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{s}</span>
                      ))}
                    </div>
                    {p.status === 'FAILED' && p.errorMessage && (
                      <p className="mt-1 text-xs text-red-600">{p.errorMessage}</p>
                    )}
                  </div>
                  {p.status === 'READY' && (
                    <div className="flex items-center gap-3">
                      {p.examZipKey && (
                        <button
                          onClick={() => download(p.id, 'exam')}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-ocean-700 hover:text-ocean-800"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" /> Exam ZIP
                        </button>
                      )}
                      {p.boardPdfKey && (
                        <button
                          onClick={() => download(p.id, 'board')}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-ocean-700 hover:text-ocean-800"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" /> Board PDF
                        </button>
                      )}
                      {p.boardDocxKey && (
                        <button
                          onClick={() => download(p.id, 'board-docx')}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-ocean-700 hover:text-ocean-800"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" /> Board DOCX
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}
